import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError, createClient } from '@connectrpc/connect';
import { CaptureTaskProvenanceService } from '@mono/proto-clients';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectTransport } from '../../lib/connect/transport';
import { dateInputValue, parseDateInput } from '../../lib/localCalendarDate';
import { PlatformUserPicker } from '../Delivery/_shared/components/PlatformUserPicker';
import { CAPTURE_KEY } from './hooks';
import { overviewUrl } from './Overview/overviewNavigation';
import { ReadOnlyScope } from './record/ReadOnlyScope';
const client = createClient(CaptureTaskProvenanceService, connectTransport);
const ids = Testids.growthShape04;
export function CustomerFollowUps({ captureProjectId, sourceKind, sourceId, statement, readOnly = false }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const key = [
        CAPTURE_KEY,
        captureProjectId,
        'follow-ups',
        sourceKind,
        sourceId
    ];
    const query = useQuery({
        queryKey: key,
        queryFn: ()=>client.listCustomerFollowUps({
                captureProjectId,
                sourceKind,
                sourceId
            })
    });
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState(statement.slice(0, 512));
    const [owner, setOwner] = useState('');
    const [due, setDue] = useState('');
    const busy = useRef(false);
    const adopt = useMutation({
        mutationFn: ()=>{
            const date = parseDateInput(due);
            if (due && (!date || dateInputValue(date) !== due)) {
                throw new Error('Choose a valid due date.');
            }
            return client.adoptCustomerFollowUp({
                captureProjectId,
                sourceKind,
                sourceId,
                statement,
                title,
                ownerUserId: owner,
                dueAt: date ? Timestamp.fromDate(date) : undefined
            });
        },
        onSuccess: async ()=>{
            setOpen(false);
            await queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    captureProjectId
                ]
            });
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-task-provenance',
                    captureProjectId
                ]
            });
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-plan',
                    captureProjectId
                ]
            });
        }
    });
    if (query.isPending) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading follow-ups…"
    });
    if (query.error) return /*#__PURE__*/ _jsx(SpecraCard, {
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: ConnectError.from(query.error).code === Code.NotFound ? 'Follow-ups unavailable for this source.' : 'Could not load follow-ups.'
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "outline",
                    onClick: ()=>void query.refetch(),
                    children: "Retry follow-ups"
                })
            ]
        })
    });
    const existing = query.data.followUps.find((f)=>f.statement === statement.trim());
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsx(SpecraCard, {
            children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                children: [
                    query.data.followUps.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                        tone: "muted",
                        children: "No assigned follow-ups. Notes remain unassigned until you create a task."
                    }),
                    query.data.followUps.map((f)=>/*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    "data-testid": ids.context,
                                    children: f.statement
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    tone: "muted",
                                    children: f.contextSnapshot
                                }),
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "neutral",
                                    "data-testid": ids.status,
                                    children: f.task?.status
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    "data-testid": ids.openTask,
                                    variant: "outline",
                                    onClick: ()=>navigate(overviewUrl(`/capture/${captureProjectId}`, {
                                            kind: 'task',
                                            id: f.task?.id ?? ''
                                        })),
                                    children: [
                                        "Open Task: ",
                                        f.task?.title
                                    ]
                                })
                            ]
                        }, f.task?.id)),
                    !!statement.trim() && !existing && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": ids.createTask,
                        onClick: ()=>{
                            setTitle(statement.slice(0, 512));
                            adopt.reset();
                            setOpen(true);
                        },
                        children: "Create Task"
                    }),
                    /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                        open: open,
                        onOpenChange: setOpen,
                        title: "Confirm follow-up task",
                        description: "Choose who owns it and when it is due.",
                        footer: /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.confirm,
                            disabled: !title.trim() || !owner || adopt.isPending,
                            isLoading: adopt.isPending,
                            onClick: async ()=>{
                                if (busy.current) return;
                                busy.current = true;
                                try {
                                    await adopt.mutateAsync();
                                } catch  {
                                /* Show the retained form and error below. */ } finally{
                                    busy.current = false;
                                }
                            },
                            children: "Create Task"
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                "data-testid": ids.context,
                                children: statement
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Task title",
                                htmlFor: "followup-title",
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: "followup-title",
                                    "data-testid": ids.title,
                                    value: title,
                                    maxLength: 512,
                                    onChange: (e)=>setTitle(e.target.value)
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Owner",
                                htmlFor: "followup-owner",
                                children: /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                    id: "followup-owner",
                                    value: owner,
                                    onChange: setOwner,
                                    testid: ids.ownerSearch,
                                    triggerTestid: ids.owner
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Due date (optional)",
                                htmlFor: "followup-due",
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: "followup-due",
                                    type: "date",
                                    "data-testid": ids.due,
                                    value: due,
                                    onChange: (e)=>setDue(e.target.value)
                                })
                            }),
                            adopt.error && /*#__PURE__*/ _jsx(SpecraText, {
                                tone: "danger",
                                children: "Could not confirm the task. Your choices are retained; retry Create Task."
                            })
                        ]
                    })
                ]
            })
        })
    });
}
